import React from 'react';
import { MediaActions } from '../../ui/buttons/media-actions';
import { useResponsiveSizes } from '../../../hooks/use-responsive-sizes';
import { Resolutions, ResultType } from '../../../types/banner';
import { RESOLUTION_LABELS } from '../../../constants/channels';

export const VERTICAL_RESOLUTIONS: Resolutions[] = [
  '1080x1920',
  '1080x1350',
  '300x600',
  '310x482',
  '400x600',
  '960x1200',
  '9:16',
  '4:5'
];

export const HORIZONTAL_SQUARE_RESOLUTIONS: Resolutions[] = [
  '1920x1080',
  '1200x628',
  '1080x1080',
  '1200x1200',
  '1:1',
  '300x250'
];

export const getResolutionMaxWidth = (format?: string): string => {
  if (!format) return '60%';

  if (VERTICAL_RESOLUTIONS.includes(format as Resolutions)) {
    return '50%';
  }

  return '80%';
};

export const MediaPreview: React.FC<{
  type: ResultType;
  src: string;
  format?: string;
}> = ({ type, src, format }) => {
  const { spacing } = useResponsiveSizes();
  const [isLoading, setIsLoading] = React.useState(true);
  const [isHovered, setIsHovered] = React.useState(false);

  const isResolution = format && format in RESOLUTION_LABELS;
  const isImage = type === 'image' || type === 'gif';

  const maxWidth = getResolutionMaxWidth(format);

  return (
    <div
      style={{
        width: maxWidth,
        height: '60%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: spacing.xs
      }}
    >
      {/* Label de resolución */}
      {isResolution && (
        <div
          style={{
            fontSize: 12,
            color: '#4b5563',
            letterSpacing: '0.5px',
            marginBottom: spacing.xs,
            textTransform: 'uppercase',
            fontWeight: 600
          }}
        >
          {format}
        </div>
      )}

      {isLoading && (
        <div
          style={{
            width: '100%',
            minHeight: 240,
            background: '#f8fafc',
            borderRadius: 12,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <div
            style={{
              width: 40,
              height: 40,
              border: '2px solid #e2e8f0',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite'
            }}
          />
        </div>
      )}

      {/* Contenedor de media */}
      <div
        style={{
          position: 'relative',
          maxWidth: '100%',
          maxHeight: '100%',
          display: isLoading ? 'none' : 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: 12,
          overflow: 'visible',
          cursor: 'pointer'
        }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {/* Imagen o Video */}
        {isImage ? (
          <img
            src={src}
            alt='Asset preview'
            style={{
              maxWidth: '100%',
              maxHeight: '100%',
              width: 'auto',
              height: 'auto',
              objectFit: 'contain',
              borderRadius: 12,
              display: 'block',
              transition: 'filter 0.3s ease, box-shadow 0.3s ease',
              filter: isHovered ? 'brightness(1.1)' : 'brightness(1)',
              boxShadow: isHovered ? '0 12px 40px rgba(0, 0, 0, 0.25)' : '0 8px 25px rgba(0, 0, 0, 0.15)'
            }}
            onLoad={() => setIsLoading(false)}
          />
        ) : (
          <video
            src={src}
            controls
            style={{
              maxWidth: '100%',
              maxHeight: '100%',
              width: 'auto',
              height: 'auto',
              objectFit: 'contain',
              borderRadius: 12,
              display: 'block',
              transition: 'filter 0.3s ease, box-shadow 0.3s ease',
              filter: isHovered ? 'brightness(1.1)' : 'brightness(1)',
              boxShadow: isHovered ? '0 12px 40px rgba(0, 0, 0, 0.25)' : '0 8px 25px rgba(0, 0, 0, 0.15)'
            }}
            onLoadedMetadata={() => setIsLoading(false)}
          />
        )}

        {/* Overlay con botones */}
        {!isLoading && (
          <div
            style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: spacing.sm,
              padding: spacing.md,
              borderRadius: 16,
              background: isHovered ? 'rgba(0, 0, 0, 0.7)' : 'transparent',
              backdropFilter: isHovered ? 'blur(8px)' : 'none',
              opacity: isHovered ? 1 : 0,
              transition: 'all 0.3s ease',
              pointerEvents: isHovered ? 'auto' : 'none',
              zIndex: 3
            }}
          >
            <MediaActions type={type} src={src} isHovered={isHovered} />
          </div>
        )}
      </div>

      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};
